/**
 * Chiamate alle API dei repository GitHub delle release (ADR 0039 §2), per gli
 * amministratori della piattaforma.
 *
 * La cache dei pacchetti scarica le release di GitHub solo per i repository ammessi:
 * un elenco fisso, gestito nel repository di institor, e un elenco che cresce con le
 * proposte degli agenti. Un amministratore approva o rifiuta con un clic; un rifiuto
 * dopo l'approvazione la revoca.
 *
 * Provvisorio dichiarato: percorsi e tipi scritti a mano (CLAUDE.md §2.3), presidiati da
 * `releaseRepos.test.ts`.
 */

import type { ApiClient } from '../client';

/** Stato di un repository proposto. */
export type ReleaseRepoState = 'proposed' | 'approved' | 'rejected';

/** Un repository dell'elenco che cresce con le proposte. */
export interface ReleaseRepo {
  /** `owner/nome` in minuscolo. */
  repo: string;
  /** Stato. */
  state: ReleaseRepoState;
  /** Motivo dell'agente: testo semplice, da mostrare come dato. */
  reason: string;
  /** Chi ha proposto (proprietario del set). */
  proposed_by: string;
  /** Set da cui è arrivata la proposta. */
  proposed_from_set: string;
  /** Ultima proposta, ISO 8601. */
  proposed_at: string;
  /** Chi ha deciso; vuoto se in attesa. */
  decided_by: string;
  /** Decisione, ISO 8601. */
  decided_at: string | null;
  /** Nota della decisione. */
  decision_note: string;
}

/** Elenco per la pagina dell'amministrazione. */
export interface ReleaseRepoList {
  /** Repository proposti, approvati e rifiutati, i più recenti prima. */
  items: ReleaseRepo[];
  /** Elenco fisso, sempre ammesso. */
  static: string[];
}

/**
 * Percorso di una decisione.
 *
 * @param repo `owner/nome`.
 * @param decision `approve` o `reject`.
 * @returns Il percorso, con le due parti codificate.
 */
function decisionPath(repo: string, decision: 'approve' | 'reject'): string {
  const [owner = '', name = ''] = repo.split('/');
  return `/api/v1/tools/release-repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/${decision}`;
}

/** Operazioni sui repository delle release, esposte alla UI. */
export class ReleaseReposApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Elenca i repository.
   *
   * @returns Elenco con le proposte ed elenco fisso.
   * @throws ApiError 403 per chi non è amministratore.
   */
  list(): Promise<ReleaseRepoList> {
    return this.client.request<ReleaseRepoList>('GET', '/api/v1/tools/release-repos');
  }

  /**
   * Approva un repository: la cache lo serve subito a tutti i set.
   *
   * @param repo `owner/nome`.
   * @returns Il repository dopo la decisione.
   */
  approve(repo: string): Promise<ReleaseRepo> {
    return this.client.request<ReleaseRepo>('POST', decisionPath(repo, 'approve'), {});
  }

  /**
   * Rifiuta un repository proposto, o revoca uno approvato.
   *
   * @param repo `owner/nome`.
   * @returns Il repository dopo la decisione.
   */
  reject(repo: string): Promise<ReleaseRepo> {
    return this.client.request<ReleaseRepo>('POST', decisionPath(repo, 'reject'), {});
  }
}
